import React, { useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import useDashboardStore from '../stores/dashboardStore';

import LoadingSkeleton from '../components/dashboard/LoadingSkeleton';
import ProfileForm from '../components/dashboard/ProfileForm';
import MatchCard from '../components/dashboard/MatchCard';
import MatchingSettings from '../components/dashboard/MatchingSettings';
import ProfileCompletion from '../components/dashboard/ProfileCompletion';
import AccountDangerZone from '../components/dashboard/AccountDangerZone';

import './Dashboard.css';

const Dashboard = () => {
    const { t } = useTranslation();
    const location = useLocation();

    const isLoading = useDashboardStore(s => s.isLoading);
    const formData = useDashboardStore(s => s.formData);
    const setFormData = useDashboardStore(s => s.setFormData);
    const user = useDashboardStore(s => s.user);
    const initialFormData = useDashboardStore(s => s.initialFormData);
    const showCompletionSuccess = useDashboardStore(s => s.showCompletionSuccess);
    const wasIncomplete = useDashboardStore(s => s.wasIncomplete);
    const setShowCompletionSuccess = useDashboardStore(s => s.setShowCompletionSuccess);
    const setWasIncomplete = useDashboardStore(s => s.setWasIncomplete);
    const autoSaveProfile = useDashboardStore(s => s.autoSaveProfile);
    const fetchInitialData = useDashboardStore(s => s.fetchInitialData);
    const fetchCommunities = useDashboardStore(s => s.fetchCommunities);
    const isSaving = useDashboardStore(s => s.isSaving);
    const showSaveIndicator = useDashboardStore(s => s.showSaveIndicator);
    const message = useDashboardStore(s => s.message);
    const setMessage = useDashboardStore(s => s.setMessage);
    const openModal = useDashboardStore(s => s.openModal);
    const setTelegramConnect = useDashboardStore(s => s.setTelegramConnect);
    const resetTelegramConnect = useDashboardStore(s => s.resetTelegramConnect);

    // 1. Initial data fetch
    useEffect(() => {
        fetchInitialData();
    }, [fetchInitialData]);

    // 2. Fetch communities when user changes
    useEffect(() => {
        fetchCommunities();
    }, [user, fetchCommunities]);

    // 3. Check for Telegram connect URL params
    useEffect(() => {
        const searchString = location.search || window.location.search;
        const params = new URLSearchParams(searchString);
        const connectCode = params.get('connectCode');
        const connectUser = params.get('connectUser');

        if (connectCode && connectUser) {
            setTelegramConnect({
                username: connectUser,
                otp: connectCode,
                step: 2,
                codeFromUrl: true,
            });
            openModal('telegramConnect');
            window.history.replaceState({}, '', window.location.pathname);
        }
    }, [location.search, setTelegramConnect, openModal]);

    // Computed values (must be before profile completion effect)
    const completionFields = useMemo(() => [
        { key: 'telegram', label: t('dashboard.connected_accounts.telegram', 'Telegram'), done: !!(user?.username || user?.telegramConnected) },
        { key: 'name', label: t('dashboard.profile.name', 'Name'), done: !!formData.name?.trim() },
        { key: 'family', label: t('dashboard.profile.family', 'Family (Last Name)'), done: !!formData.family?.trim() },
    ], [formData, t, user]);

    const improvementFields = useMemo(() => [
        { key: 'country', label: t('dashboard.profile.country', 'Country'), done: !!formData.country },
        { key: 'city', label: t('dashboard.profile.city', 'City'), done: !!formData.city },
        { key: 'timezone', label: t('dashboard.profile.timezone', 'Time Zone'), done: !!formData.timezone },
        { key: 'bestMeetingDays', label: t('dashboard.profile.best_days', 'Best Meeting Days'), done: formData.bestMeetingDays?.length > 0 },
        { key: 'languages', label: t('dashboard.profile.languages', 'Languages'), done: formData.languages?.length > 0 },
        { key: 'profession', label: t('dashboard.profile.profession', 'Profession'), done: !!formData.profession?.trim() },
        { key: 'grade', label: t('dashboard.profile.grade', 'Grade'), done: !!formData.grade },
        { key: 'professionalDesc', label: t('dashboard.profile.professional_desc', 'Professional Description'), done: !!formData.professionalDesc?.trim() },
        { key: 'personalDesc', label: t('dashboard.profile.personal_desc', 'Personal Description'), done: !!formData.personalDesc?.trim() },
        { key: 'professionalInterests', label: t('dashboard.profile.professional_interests', 'Professional Interests'), done: formData.professionalInterests?.length > 0 || !!formData.otherProfessionalInterests?.trim() },
        { key: 'personalInterests', label: t('dashboard.profile.personal_interests', 'Personal Interests'), done: formData.personalInterests?.length > 0 || !!formData.otherPersonalInterests?.trim() },
        { key: 'coffeeGoals', label: t('dashboard.profile.coffee_goals', 'Your coffee goals'), done: formData.coffeeGoals?.length > 0 },
    ], [formData, t]);

    const allFieldsDone = completionFields.every(f => f.done);
    const allImprovementsDone = improvementFields.every(f => f.done);
    const showCompletionBlock = !allFieldsDone || showCompletionSuccess;

    // 4. Profile completion logic
    useEffect(() => {
        if (!initialFormData) return;
        // Frozen account (pending deletion): never auto-toggle participation.
        if (formData.deletionScheduledAt) return;

        if (!allFieldsDone) {
            setWasIncomplete(true);
            setShowCompletionSuccess(false);
            if (formData.nextWeekStatus !== 'Passive') {
                const newData = { ...formData, nextWeekStatus: 'Passive' };
                setFormData(newData);
                autoSaveProfile(newData, 'nextWeekStatus');
            }
        } else if (allFieldsDone && wasIncomplete) {
            setShowCompletionSuccess(true);
            if (formData.nextWeekStatus !== 'Active') {
                const newData = { ...formData, nextWeekStatus: 'Active' };
                setFormData(newData);
                autoSaveProfile(newData, 'nextWeekStatus');
            }
            const timer = setTimeout(() => {
                setShowCompletionSuccess(false);
                setWasIncomplete(false);
            }, 5000);
            return () => clearTimeout(timer);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [allFieldsDone, wasIncomplete, formData.nextWeekStatus, formData, initialFormData]);

    // 5. Auto-dismiss success messages
    useEffect(() => {
        if (message.type === 'success' && message.text) {
            const timer = setTimeout(() => setMessage({ type: '', text: '' }), 2000);
            return () => clearTimeout(timer);
        }
    }, [message, setMessage]);

    // 6. handleConnectTelegram
    const handleConnectTelegram = () => {
        resetTelegramConnect();
        openModal('telegramConnect');
    };

    if (isLoading) return <LoadingSkeleton />;

    return (
        <main className="main-content dashboard-main">
            <div className="dashboard-container">
                {/* Left Column */}
                <div className="left-column">
                    <ProfileForm />
                </div>

                {/* Right Column */}
                <div className="matching-section">
                    <MatchCard />

                    <ProfileCompletion
                        completionFields={completionFields}
                        improvementFields={improvementFields}
                        allFieldsDone={allFieldsDone}
                        allImprovementsDone={allImprovementsDone}
                        showCompletionBlock={showCompletionBlock}
                        onConnectTelegram={handleConnectTelegram}
                    />

                    <MatchingSettings allFieldsDone={allFieldsDone} />

                    <AccountDangerZone />
                </div>
            </div>

            {/* Sticky Save Indicator */}
            {showSaveIndicator && (
                <div className={`sticky-save-indicator ${isSaving ? 'saving' : ''}`}>
                    {isSaving ? (
                        <>
                            <svg
                                width="16"
                                height="16"
                                viewBox="0 0 24 24"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                                style={{ animation: 'spin 1s linear infinite' }}
                            >
                                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" opacity="0.25"/>
                                <path d="M12 2a10 10 0 0 1 10 10" stroke="currentColor" strokeWidth="4" strokeLinecap="round"/>
                            </svg>
                            {t('common.saving', 'Saving...')}
                        </>
                    ) : (
                        <>
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M13.5 4L6 11.5L2.5 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                            {t('common.all_changes_saved', 'All changes saved')}
                        </>
                    )}
                </div>
            )}
        </main>
    );
};

export default Dashboard;
